import { useEffect, useRef, useState } from "react";

interface PageHeroProps {
  title: React.ReactNode;
  description: string;
  blobPosition?: "left" | "right" | "center";
}

const PageHero = ({ title, description, blobPosition = "left" }: PageHeroProps) => {
  const sectionRef = useRef<HTMLElement | null>(null);
  const [pointer, setPointer] = useState({ x: 0, y: 0 });

  useEffect(() => {
    const prefersReducedMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)"
    ).matches;
    if (prefersReducedMotion) return;

    const el = sectionRef.current;
    if (!el) return;

    let ticking = false;
    let nextX = 0;
    let nextY = 0;

    const apply = () => {
      setPointer({ x: nextX, y: nextY });
      ticking = false;
    };

    const onMove = (e: PointerEvent) => {
      const rect = el.getBoundingClientRect();
      const halfW = rect.width / 2;
      const halfH = rect.height / 2;
      const nx = (e.clientX - rect.left - halfW) / halfW;
      const ny = (e.clientY - rect.top - halfH) / halfH;
      nextX = Math.min(1, Math.max(-1, nx));
      nextY = Math.min(1, Math.max(-1, ny));
      if (!ticking) {
        ticking = true;
        requestAnimationFrame(apply);
      }
    };

    const onLeave = () => {
      nextX = 0;
      nextY = 0;
      if (!ticking) {
        ticking = true;
        requestAnimationFrame(apply);
      }
    };

    el.addEventListener("pointermove", onMove);
    el.addEventListener("pointerleave", onLeave);
    return () => {
      el.removeEventListener("pointermove", onMove);
      el.removeEventListener("pointerleave", onLeave);
    };
  }, []);

  const { x: nx, y: ny } = pointer;
  const blobTransition =
    "transform 500ms cubic-bezier(0.22, 1, 0.36, 1), filter 500ms ease-out";

  const topBlobClass =
    blobPosition === "right"
      ? "-top-32 -right-32"
      : blobPosition === "center"
      ? "-top-32 left-1/4"
      : "-top-32 -left-32";
  const bottomBlobClass =
    blobPosition === "right"
      ? "-bottom-32 -left-32"
      : blobPosition === "center"
      ? "-bottom-32 right-1/4"
      : "-bottom-32 -right-32";

  return (
    <section
      ref={sectionRef}
      className="relative py-16 sm:py-24 md:py-36 lg:py-40 overflow-hidden bg-background"
    >
      <div
        aria-hidden
        className={`absolute ${topBlobClass} w-[28rem] h-[28rem] rounded-full bg-primary/15 blur-3xl will-change-transform`}
        style={{
          transform: `translate3d(${nx * 60}px, ${ny * 50}px, 0) scale(${1 + Math.abs(nx) * 0.08})`,
          filter: `blur(48px) hue-rotate(${nx * 12}deg)`,
          transition: blobTransition,
        }}
      />
      <div
        aria-hidden
        className={`absolute ${bottomBlobClass} w-[28rem] h-[28rem] rounded-full blur-3xl will-change-transform`}
        style={{
          backgroundColor: "hsl(var(--accent-violet) / 0.28)",
          transform: `translate3d(${nx * -80}px, ${ny * -65}px, 0) scale(${1 + Math.abs(ny) * 0.1})`,
          filter: `blur(48px) hue-rotate(${nx * -16}deg)`,
          transition: blobTransition,
        }}
      />
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-primary/20 to-transparent" />
      <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-primary/30 to-transparent" />

      <div className="relative z-10 container mx-auto px-4 sm:px-6 max-w-4xl text-center">
        <h1 className="text-3xl sm:text-4xl lg:text-6xl font-bold mb-6 leading-tight animate-fade-in-up">
          {title}
        </h1>
        <p
          className="text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto animate-fade-in-up"
          style={{ animationDelay: "120ms" }}
        >
          {description}
        </p>
      </div>
    </section>
  );
};

export default PageHero;
